import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Maximize2, Minimize2, ChevronLeft, ChevronRight, Send, X } from 'lucide-react';
import { ToggleControls } from './ToggleControls';
import { fetchRandomFirstSessionPair, HumanDatasetPair } from '../../services/humanDatasetService';
import { SendMessageOptions } from '../../types/messaging';
import { InlineColoredIonoff } from '../ui/ColoredIonoffText';
import { MultiColorIonoff } from '../ui/MultiColorIonoff';
import { HelpTooltip, HoverHelpTooltip } from '../ui/HelpTooltip';

interface ChatInputProps {
  onSendMessage: (message: string, plusHumanContent?: string, options?: SendMessageOptions) => void;
  onStop?: () => void;
  isProcessing?: boolean;
  disabled?: boolean;
  canSendNextPrompt?: boolean; // 🎯 PHASE 1: P(n+1) can only be sent after P(n) first response
  isInitialState?: boolean;
  placeholder?: string;
  // 🎯 PHASE 2: Prompt editing
  initialMessage?: string; // Prepopulate input with this text
  editingPromptId?: string | null; // If editing, the ID of the original prompt
  onClearEdit?: () => void; // Clear edit state
  // Toggle states
  webEnabled: boolean;
  setWebEnabled: (value: boolean) => void;
  enterSendsEnabled: boolean;
  setEnterSendsEnabled: (value: boolean) => void;
  selfEvalEnabled: boolean;
  setSelfEvalEnabled: (value: boolean) => void;
  txtEnabled: boolean;
  setTxtEnabled: (value: boolean) => void;
  // +Human feature
  plusHumanEnabled: boolean;
  setPlusHumanEnabled: (value: boolean) => void;
  humanLeaderboardEnabled: boolean;
  setHumanLeaderboardEnabled: (value: boolean) => void;
  autoHumanDatasetEnabled?: boolean;
  // Tools expanded state (controls visibility of other toggle buttons)
  toolsExpanded: boolean;
  setToolsExpanded: (value: boolean) => void;
  // Target count for conditional button visibility
  targetCount?: number;
  // Admin feature
  showAdminInput?: boolean;
  setShowAdminInput?: (value: boolean) => void;
  adminConversationCount?: string;
  setAdminConversationCount?: (value: string) => void;
  onLoadAdminConversations?: (count: number) => void;
  isLoadingAdminConversations?: boolean;
  // Production mode - hides dev-only controls
  prodMode?: boolean;
  // Help me start feature - shows icebreaker prompts
  onHelpMeStart?: () => void;
  // Whether icebreakers are currently being shown (for button contrast)
  isShowingIcebreakers?: boolean;
  // Current icebreaker message (to prepend when user starts typing)
  currentIcebreaker?: string;
  // Tag colors for ionoff logo (from active AI models)
  tagColors?: string[];
  // Callback when input text changes (for parent to track if user is typing)
  onInputChange?: (hasText: boolean) => void;
  // New conversation feature - replaces "help me start" after first message
  onNewConversation?: () => void;
  // Thinking/reasoning effort control
  reasoningEffort?: 'default' | 'low' | 'medium' | 'high';
  setReasoningEffort?: (value: 'default' | 'low' | 'medium' | 'high') => void;
  // Postscript intro text from config (for icebreaker context)
  postscriptIntro?: string;
  // Custom review postscript intro text from config
  customreviewPostscriptIntro?: string;
  // Active custom rubric (for custom review postscript)
  activeRubric?: {
    name: string;
    descriptions: {
      A: string;
      B: string;
      C: string;
      D: string;
      F: string;
    };
  } | null;
  // Questionmark help system
  getHelpText?: (label: string) => string | null;
  hasHelp?: (label: string) => boolean;
  shouldShowHelp?: (label: string, hasHelpConfig: boolean) => boolean;
  markInteracted?: (label: string) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({ 
  onSendMessage, 
  onStop,
  isProcessing = false,
  disabled = false, 
  canSendNextPrompt = true, // Default to true for backward compatibility
  isInitialState = false,
  placeholder = "add context to this conversation",
  initialMessage,
  editingPromptId,
  onClearEdit,
  webEnabled,
  setWebEnabled,
  enterSendsEnabled,
  setEnterSendsEnabled,
  selfEvalEnabled,
  setSelfEvalEnabled,
  txtEnabled,
  setTxtEnabled,
  plusHumanEnabled,
  setPlusHumanEnabled,
  humanLeaderboardEnabled,
  setHumanLeaderboardEnabled,
  toolsExpanded,
  setToolsExpanded,
  targetCount = 3,
  autoHumanDatasetEnabled = false,
  showAdminInput,
  setShowAdminInput,
  adminConversationCount,
  setAdminConversationCount,
  onLoadAdminConversations,
  isLoadingAdminConversations,
  prodMode = false,
  onHelpMeStart,
  isShowingIcebreakers = false,
  currentIcebreaker,
  tagColors = [],
  onInputChange,
  onNewConversation,
  reasoningEffort = 'default',
  setReasoningEffort,
  postscriptIntro = 'postscript: the user-generated language above was authored in context of the following:',
  customreviewPostscriptIntro = 'postscript. generate an excellent response to the above prompt as defined by the following rubric:',
  activeRubric = null,
  getHelpText,
  hasHelp,
  shouldShowHelp,
  markInteracted,
}) => {
  const [message, setMessage] = useState('');
  const [plusHumanContent, setPlusHumanContent] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoHumanPair, setAutoHumanPair] = useState<HumanDatasetPair | null>(null);
  const datasetRequestRef = useRef<Promise<HumanDatasetPair | null> | null>(null);
  // Track if icebreaker has already been added as postscript to avoid duplicate adding
  const icebreakerPrependedRef = useRef(false);
  // Postscript state: stores the icebreaker context that appears below user text
  // When isHighlighted is true, it renders as a visually distinct block with X dismiss
  // When user clicks into it, isHighlighted becomes false and it merges into main text
  const [postscript, setPostscript] = useState<{ text: string; isHighlighted: boolean } | null>(null);
  // Custom review postscript state: stores the rubric context when custom review is active
  const customReviewAddedRef = useRef(false);
  const [customReviewPostscript, setCustomReviewPostscript] = useState<{ text: string; isHighlighted: boolean } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const plusHumanTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Notify parent when input text changes (for showing/hiding welcome suffix)
  useEffect(() => {
    onInputChange?.(!!message.trim());
  }, [message, onInputChange]);

  // Reset icebreaker prepended flag and postscript when switching modes or changing icebreaker
  useEffect(() => {
    icebreakerPrependedRef.current = false;
    setPostscript(null);
  }, [isShowingIcebreakers, currentIcebreaker]);

  // Reset custom review postscript when rubric changes
  useEffect(() => {
    customReviewAddedRef.current = false;
    setCustomReviewPostscript(null);
  }, [activeRubric?.name]);

  // Detect if device is mobile or tablet
  const isMobileOrTablet = () => {
    // Check for touch support
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Check user agent for mobile/tablet
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    
    // Check screen size (tablets typically have larger screens)
    const isSmallScreen = window.innerWidth < 768;
    
    return hasTouch && (isMobileUA || isSmallScreen);
  };

  const submitMessage = () => {
    if (!message.trim() || disabled) {
      return;
    }

    if ((plusHumanEnabled || !!autoHumanPair) && !plusHumanContent.trim()) {
      return; // Don't send if +human (or dataset) is enabled but empty
    }

    const options: SendMessageOptions | undefined = autoHumanPair
      ? {
          promptSourceOverride: 'dataset',
          plusHumanEnabled: true,
          selfEvalEnabled: true,
          humanLeaderboardEnabled,
          humanDatasetCardId: autoHumanPair.cardId,
          humanDatasetDocId: autoHumanPair.docId,
          humanDatasetSessionId: autoHumanPair.sessionId,
          humanDatasetSessionTitle: autoHumanPair.sessionTitle,
        }
      : editingPromptId
      ? {
          // 🎯 PHASE 2: Include edit metadata
          isEditOf: editingPromptId,
        }
      : undefined;

    // Combine message with postscripts if present (highlighted or merged)
    // Order: user message, then icebreaker postscript, then custom review postscript
    let finalMessage = message.trim();
    if (postscript) {
      const postscriptText = `\n\n${postscriptIntro} ${postscript.text}`;
      finalMessage = finalMessage + postscriptText;
    }
    if (customReviewPostscript) {
      finalMessage = finalMessage + `\n\n${customReviewPostscript.text}`;
    }

    onSendMessage(finalMessage, plusHumanContent.trim() || undefined, options);
    setMessage('');
    setPlusHumanContent('');
    setAutoHumanPair(null);
    setPostscript(null);
    setCustomReviewPostscript(null);
    datasetRequestRef.current = null;
    icebreakerPrependedRef.current = false;
    customReviewAddedRef.current = false;
    onClearEdit?.(); // Clear edit state after sending
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMessage();
  };

  // Development-only: Expose setMessage and submit to window for browser automation
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).__ionoffSetMessage = (text: string) => {
        setMessage(text);
        // Trigger React's onChange handler properly
        const textarea = textareaRef.current;
        if (textarea) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLTextAreaElement.prototype,
            'value'
          )?.set;
          if (nativeInputValueSetter) {
            nativeInputValueSetter.call(textarea, text);
            const event = new Event('input', { bubbles: true });
            textarea.dispatchEvent(event);
          }
        }
      };
      (window as any).__ionoffSetPlusHumanContent = (text: string) => {
        setPlusHumanContent(text);
        const textarea = plusHumanTextareaRef.current;
        if (textarea) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLTextAreaElement.prototype,
            'value'
          )?.set;
          if (nativeInputValueSetter) {
            nativeInputValueSetter.call(textarea, text);
            const event = new Event('input', { bubbles: true });
            textarea.dispatchEvent(event);
          }
        }
      };
      (window as any).__ionoffTogglePlusHuman = (value?: boolean) => {
        const next = typeof value === 'boolean' ? value : !plusHumanEnabled;
        setPlusHumanEnabled(next);
        if (next && !selfEvalEnabled) {
          setSelfEvalEnabled(true);
        }
      };
      (window as any).__ionoffSubmitMessage = () => {
        submitMessage();
      };
      (window as any).__ionoffGetInputState = () => ({
        message,
        plusHumanContent,
        plusHumanEnabled,
        disabled,
        isProcessing,
      });
      return () => {
        delete (window as any).__ionoffSetMessage;
        delete (window as any).__ionoffSetPlusHumanContent;
        delete (window as any).__ionoffTogglePlusHuman;
        delete (window as any).__ionoffSubmitMessage;
        delete (window as any).__ionoffGetInputState;
      };
    }
  }, [message, plusHumanContent, plusHumanEnabled, selfEvalEnabled, disabled, onSendMessage, isProcessing]);

  // 🎯 PHASE 2: Handle prompt editing - populate input with initial message
  useEffect(() => {
    if (initialMessage) {
      setMessage(initialMessage);
      // Auto-focus the textarea
      setTimeout(() => {
        textareaRef.current?.focus();
        // Place cursor at end of text
        if (textareaRef.current) {
          const len = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(len, len);
        }
      }, 100);
    }
  }, [initialMessage]);

  // Auto-resize textarea to fit content (up to max height)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && !isFullscreen) {
      // Reset height to auto to get accurate scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight, capped at 300px
      const newHeight = Math.min(textarea.scrollHeight, 300);
      textarea.style.height = `${newHeight}px`;
      // Show scrollbar only if content exceeds max
      textarea.style.overflowY = textarea.scrollHeight > 300 ? 'auto' : 'hidden';
    }
  }, [message, isFullscreen]);

  useEffect(() => {
    const textarea = plusHumanTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [plusHumanContent]);

  useEffect(() => {
    if (!autoHumanDatasetEnabled || !plusHumanEnabled) {
      if (!plusHumanEnabled) {
        setAutoHumanPair(null);
      }
      return;
    }

    if (message.trim() || plusHumanContent.trim() || !!autoHumanPair) {
      return;
    }

    let isCancelled = false;

    const loadDatasetPair = async () => {
      try {
        if (!datasetRequestRef.current) {
          datasetRequestRef.current = fetchRandomFirstSessionPair();
        }
        const pair = await datasetRequestRef.current;
        if (isCancelled) {
          return;
        }
        if (pair) {
          setMessage(pair.prompt);
          setPlusHumanContent(pair.response);
          setAutoHumanPair(pair);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to fetch human dataset pair:', error);
        }
      } finally {
        if (!isCancelled) {
          datasetRequestRef.current = null;
        }
      }
    };

    loadDatasetPair();

    return () => {
      isCancelled = true;
    };
  }, [autoHumanDatasetEnabled, plusHumanEnabled, message, plusHumanContent, autoHumanPair]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Explicitly allow Cmd/Ctrl+A for select all (don't interfere)
    if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
      // Let the default select-all behavior happen
      return;
    }
    
    if (e.key === 'Enter') {
      if (enterSendsEnabled) {
        // Enter sends message, Shift+Enter creates new line
        if (e.shiftKey) {
          // Shift+Enter creates a new line (default behavior)
          return;
        } else {
          // Enter without Shift sends the message
          e.preventDefault();
          submitMessage();
        }
      } else {
        // Enter creates new line (default behavior)
        // Cmd/Ctrl+Enter can still send
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          submitMessage();
        }
        // Otherwise, let Enter create a new line naturally
      }
    }
  };

  return (
    <div className={`${isInitialState ? 'flex-1 flex items-center justify-center' : 'sticky bottom-0 bg-background border-t border-border'}`}>
      <div className={`${isInitialState ? 'w-full' : 'max-w-3xl mx-auto p-4 md:p-6'}`}>
        {/* Toggle Controls Row - help me start + tools button always visible, other controls show when expanded */}
        <div className="flex items-center justify-center gap-2 mb-2">
          {/* "help me start" button - toggles between welcomes and icebreakers */}
          {/* Low-contrast when showing welcomes (off), high-contrast when showing icebreakers (on) */}
          {isInitialState && onHelpMeStart && (
            <button
              type="button"
              onClick={onHelpMeStart}
              className={`inline-flex items-center justify-center gap-1 px-2 h-7 rounded text-xs font-medium
                bg-gray-600 hover:scale-105 transition-all duration-200 cursor-pointer
                ${isShowingIcebreakers ? 'text-white border border-white/70 dark:border-white/70' : 'text-gray-400 border border-transparent'}`}
              title={isShowingIcebreakers ? "Switch back to welcome message" : "Get an icebreaker prompt to start a conversation"}
            >
              help me start
            </button>
          )}
          
          {/* "new conversation" button - appears after first message is sent, replaces "help me start" */}
          {!isInitialState && onNewConversation && (
            <HoverHelpTooltip
              helpText={getHelpText?.('new conversation') || ''}
              enabled={!shouldShowHelp?.('new conversation', hasHelp?.('new conversation') ?? false) && (hasHelp?.('new conversation') ?? false)}
            >
            <button
              type="button"
              onClick={() => {
                onNewConversation();
                if (markInteracted && hasHelp?.('new conversation')) markInteracted('new conversation');
              }}
              className="inline-flex items-center justify-center gap-1 px-2 h-7 rounded text-xs font-medium
                bg-gray-600 hover:scale-105 transition-all duration-200 cursor-pointer text-gray-400"
              title="Start a new conversation"
            >
              <span className="text-sm">+</span>new conversation
              {shouldShowHelp?.('new conversation', hasHelp?.('new conversation') ?? false) && (
                <HelpTooltip
                  helpText={getHelpText?.('new conversation') || ''}
                  onHelpViewed={() => markInteracted?.('new conversation')}
                  size="sm"
                />
              )}
            </button>
            </HoverHelpTooltip>
          )}
          
          {/* Prompt Settings Button - always visible, toggles visibility of other controls */}
          <button
            onClick={() => setToolsExpanded(!toolsExpanded)}
            className={`inline-flex items-center justify-center gap-1 px-2 h-7 rounded text-xs font-medium
              transition-all duration-200 cursor-pointer hover:scale-105 bg-gray-600
              ${toolsExpanded ? 'text-white' : 'text-gray-400'}`}
            title={toolsExpanded ? 'Hide prompt settings' : 'Show prompt settings'}
          >
            {/* Custom two-line vertical sliders icon */}
            <svg className="h-3 w-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="8" y1="4" x2="8" y2="20" />
              <circle cx="8" cy="9" r="2" fill="currentColor" />
              <line x1="16" y1="4" x2="16" y2="20" />
              <circle cx="16" cy="15" r="2" fill="currentColor" />
            </svg>
            <span className="flex flex-col leading-none text-[10px]">
              <span>prompt</span>
              <span>settings</span>
            </span>
          </button>
          
        </div>
        
        {/* Other Toggle Controls Row - only visible when tools is expanded */}
        {toolsExpanded && (
          <div className="flex items-center justify-center gap-2 mb-2">
            <ToggleControls
              webEnabled={webEnabled}
              setWebEnabled={setWebEnabled}
              enterSendsEnabled={enterSendsEnabled}
              setEnterSendsEnabled={setEnterSendsEnabled}
              selfEvalEnabled={selfEvalEnabled}
              setSelfEvalEnabled={setSelfEvalEnabled}
              txtEnabled={txtEnabled}
              setTxtEnabled={setTxtEnabled}
              plusHumanEnabled={plusHumanEnabled}
              setPlusHumanEnabled={setPlusHumanEnabled}
              humanLeaderboardEnabled={humanLeaderboardEnabled}
              setHumanLeaderboardEnabled={setHumanLeaderboardEnabled}
              targetCount={targetCount}
              showAdminInput={showAdminInput}
              setShowAdminInput={setShowAdminInput}
              adminConversationCount={adminConversationCount}
              setAdminConversationCount={setAdminConversationCount}
              onLoadAdminConversations={onLoadAdminConversations}
              isLoadingAdminConversations={isLoadingAdminConversations}
              prodMode={prodMode}
              reasoningEffort={reasoningEffort}
              setReasoningEffort={setReasoningEffort}
              getHelpText={getHelpText}
              hasHelp={hasHelp}
              shouldShowHelp={shouldShowHelp}
              markInteracted={markInteracted}
            />
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="relative">
          <div className="relative flex flex-col items-stretch bg-muted/50 border border-border rounded-2xl px-4 pt-3 pb-12 shadow-sm focus-within:border-primary/50 transition-all">
            {/* Multi-line text input */}
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => {
                if (autoHumanPair) {
                  setAutoHumanPair(null);
                }
                
                const newValue = e.target.value;
                
                // If user is viewing an icebreaker and starts typing, add icebreaker as postscript
                if (
                  isShowingIcebreakers && 
                  currentIcebreaker && 
                  !icebreakerPrependedRef.current && 
                  message === '' && 
                  newValue.trim() !== ''
                ) {
                  // Add icebreaker as highlighted postscript below user text
                  icebreakerPrependedRef.current = true;
                  setPostscript({ text: currentIcebreaker, isHighlighted: true });
                }
                
                // If custom review rubric is active and user starts typing, add rubric as postscript
                if (
                  activeRubric &&
                  !customReviewAddedRef.current &&
                  message === '' &&
                  newValue.trim() !== ''
                ) {
                  // Build the full rubric text: intro + all grade descriptions
                  const rubricText = `${customreviewPostscriptIntro} A: ${activeRubric.descriptions.A} B: ${activeRubric.descriptions.B} C: ${activeRubric.descriptions.C} D: ${activeRubric.descriptions.D} F: ${activeRubric.descriptions.F}`;
                  customReviewAddedRef.current = true;
                  setCustomReviewPostscript({ text: rubricText, isHighlighted: true });
                }
                
                // Reset prepended flag and postscript if message is cleared
                if (newValue === '') {
                  icebreakerPrependedRef.current = false;
                  setPostscript(null);
                  customReviewAddedRef.current = false;
                  setCustomReviewPostscript(null);
                }
                
                setMessage(newValue);
              }}
              onInput={() => {
                // Trigger resize on any input (including paste)
                const textarea = textareaRef.current;
                if (textarea && !isFullscreen) {
                  textarea.style.height = 'auto';
                  const newHeight = Math.min(textarea.scrollHeight, 300);
                  textarea.style.height = `${newHeight}px`;
                  textarea.style.overflowY = textarea.scrollHeight > 300 ? 'auto' : 'hidden';
                }
              }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              className={`w-full resize-none bg-transparent border-none outline-none focus:ring-0 py-2 placeholder:text-muted-foreground/60 ${
                isFullscreen 
                  ? 'min-h-[400px] h-[400px] overflow-auto' 
                  : 'min-h-[24px]'
              }`}
            />

            {/* Postscript Block - shows icebreaker context below user text */}
            {postscript && postscript.isHighlighted && (
              <div className="mt-2 text-left">
                <span 
                  className="bg-gray-500/20 rounded-sm px-1 py-0.5 cursor-text text-sm text-foreground/80 leading-relaxed"
                  style={{ 
                    boxDecorationBreak: 'clone',
                    WebkitBoxDecorationBreak: 'clone'
                  }}
                  onClick={() => {
                    // When user clicks into the postscript block, merge it into the main text
                    const postscriptText = `\n\n${postscriptIntro} ${postscript.text}`;
                    setMessage(prev => prev + postscriptText);
                    setPostscript(null);
                    // Focus the main textarea and place cursor at end
                    setTimeout(() => {
                      textareaRef.current?.focus();
                      if (textareaRef.current) {
                        const len = textareaRef.current.value.length;
                        textareaRef.current.setSelectionRange(len, len);
                      }
                    }, 0);
                  }}
                >
                  {postscriptIntro} {postscript.text}
                  {/* X button inline at the end of text */}
                  <button
                    type="button"
                    className="inline-flex items-center justify-center w-4 h-4 ml-1 align-middle text-foreground/50 hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPostscript(null);
                      icebreakerPrependedRef.current = false;
                    }}
                    title="Remove context"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              </div>
            )}

            {/* Custom Review Postscript Block - shows rubric context when custom review is active */}
            {customReviewPostscript && customReviewPostscript.isHighlighted && (
              <div className="mt-2 text-left">
                <span 
                  className="bg-pink-500/20 rounded-sm px-1 py-0.5 cursor-text text-sm text-foreground/80 leading-relaxed"
                  style={{ 
                    boxDecorationBreak: 'clone',
                    WebkitBoxDecorationBreak: 'clone'
                  }}
                  onClick={() => {
                    // When user clicks into the postscript block, merge it into the main text
                    setMessage(prev => prev + `\n\n${customReviewPostscript.text}`);
                    setCustomReviewPostscript(null);
                    // Focus the main textarea and place cursor at end
                    setTimeout(() => {
                      textareaRef.current?.focus();
                      if (textareaRef.current) {
                        const len = textareaRef.current.value.length;
                        textareaRef.current.setSelectionRange(len, len);
                      }
                    }, 0);
                  }}
                >
                  {customReviewPostscript.text}
                  {/* X button inline at the end of text */}
                  <button
                    type="button"
                    className="inline-flex items-center justify-center w-4 h-4 ml-1 align-middle text-foreground/50 hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomReviewPostscript(null);
                      customReviewAddedRef.current = false;
                    }}
                    title="Remove custom review context"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              </div>
            )}

            {/* +Human Response Textarea - only visible when +human is ON and prompt has text */}
            {plusHumanEnabled && message.trim() && (
              <div className="mt-3 pt-3 border-t border-border/50">
                {autoHumanPair && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200">
                      Prefilled from dataset
                    </span>
                  </div>
                )}
                <textarea
                  ref={plusHumanTextareaRef}
                  value={plusHumanContent}
                  onChange={(e) => {
                    if (autoHumanPair) {
                      setAutoHumanPair(null);
                    }
                    setPlusHumanContent(e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="what say you? how does your own prompt response stack up with the AIs?"
                  disabled={disabled}
                  className="w-full resize-none bg-transparent border-none outline-none focus:ring-0 py-2 placeholder:text-muted-foreground/60 transition-all min-h-[60px] max-h-[200px]"
                  rows={2}
                />
              </div>
            )}

            {/* Fullscreen toggle button - midpoint at lower left corner */}
            <div style={{ position: 'absolute', bottom: 0, left: 0, transform: 'translate(calc(-50% + 2px), calc(50% - 2px))' }}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="flex-shrink-0 h-9 w-9 p-0 hover:bg-background"
                title={isFullscreen ? "Collapse" : "Expand"}
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Maximize2 className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>

            {/* Send button - lower right corner inside box, only visible when text is entered */}
            {message.trim() && (
              <div className="absolute bottom-1 right-2">
                <Button
                  type="submit"
                  size="sm"
                  disabled={
                    !message.trim() || 
                    disabled || 
                    !canSendNextPrompt ||
                    ((plusHumanEnabled || !!autoHumanPair) && !plusHumanContent.trim())
                  }
                  className="flex-shrink-0 h-8 w-8 p-0 rounded-full"
                  title={
                    !canSendNextPrompt
                      ? "Waiting for first response to previous prompt..."
                      : (plusHumanEnabled || !!autoHumanPair) && !plusHumanContent.trim() && message.trim()
                        ? "Please write your response before sending"
                        : "Send message"
                  }
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

